import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { alpha } from "@mui/material/styles";
import { Users } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { Autocomplete, Box, Card, CardContent, Stack, Typography } from "../ds";
import { useIsAuthorized } from "../hooks/useIsAuthorized";
import { useUserSearch } from "../hooks/useUserSearch";
import { useWorkflowConfig } from "../hooks/useWorkflow";
import { assignReviewer, invalidateSuggestionCaches } from "../api/ess";
import { apiErrorMessage } from "../utils/apiError";
import type { WorkflowCommitteeSlot } from "../api/workflow";
import type { AssignedReviewer, Suggestion } from "../types/ess";

// The picker only needs an id and something to show; the search endpoint
// returns more, and a roster entry returns less (no role), so both narrow
// to this on the way in.
interface SlotUser {
  id: string;
  name: string;
}

// One seat change in flight. Doubles as the mutation's variables type, so
// `mutation.variables` identifies which seat is saving.
interface SlotAssignment {
  committee: string;
  slot: string;
  user_id: string | null;
}

function SectionHeader({ title }: { title: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2.5 }}>
      <Box
        sx={(theme) => ({
          width: 28,
          height: 28,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "primary.main",
        })}
      >
        <RavenIcon icon={Users} sx={{ fontSize: 16 }} />
      </Box>
      <Typography
        sx={{ fontSize: 16, fontWeight: 600, color: "primary.main", flex: 1 }}
      >
        {title}
      </Typography>
    </Stack>
  );
}

interface SlotRowProps {
  slot: WorkflowCommitteeSlot;
  assigned: AssignedReviewer | undefined;
  saving: boolean;
  onPick: (user: SlotUser | null) => void;
}

function SlotRow({ slot, assigned, saving, onPick }: SlotRowProps) {
  const [query, setQuery] = useState("");
  const { results, loading } = useUserSearch(query);

  const current = useMemo<SlotUser | null>(
    () =>
      assigned
        ? { id: assigned.user_id, name: assigned.user_name ?? assigned.user_id }
        : null,
    [assigned],
  );
  // The person already in the seat was found by a search that is long
  // gone, so keep them in the option list — MUI treats a value that isn't
  // an option as invalid.
  const options = useMemo<SlotUser[]>(() => {
    const searched = results.map((r) => ({ id: r.id, name: r.name }));
    if (!current || searched.some((o) => o.id === current.id)) return searched;
    return [current, ...searched];
  }, [current, results]);

  return (
    <Autocomplete<SlotUser>
      label={slot.label}
      placeholder="Search by name or employee ID"
      options={options}
      value={current}
      loading={loading || saving}
      disabled={saving}
      noOptionsText={
        query.trim().length < 2 ? "Type at least 2 characters" : "No matches"
      }
      getOptionLabel={(u) => u.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onInputChange={setQuery}
      onChange={onPick}
    />
  );
}

interface Props {
  suggestion: Suggestion;
}

/**
 * Committee seats for one suggestion. Every committee, every slot and
 * every label comes from the workflow config's `committees` block, so the
 * frontend keeps no committee list of its own and a yaml change lands here
 * without a deploy.
 *
 * Visible only while the `assign_reviewers` auth probe passes — that
 * action exists on `submitted` alone, so the card disappears of its own
 * accord once the suggestion is forwarded. The roster itself stays
 * readable afterwards in the right rail (`RailReviewers`), which is why
 * there is no read-only mode here.
 */
export default function CommitteeCard({ suggestion }: Props) {
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data: workflow } = useWorkflowConfig();

  const authQ = useIsAuthorized(
    {
      action: "assign_reviewers",
      entity_type: "ess_suggestion",
      entity_id: suggestion.id,
      workflow: "ess_suggestion",
    },
    // Authorization flips when the suggestion leaves `submitted`, so the
    // status has to be part of the cache key (mirrors the other cards).
    [suggestion.status],
  );

  const mutation = useMutation({
    mutationFn: (vars: SlotAssignment) => assignReviewer(suggestion.id, vars),
    onSuccess: (fresh, vars) => {
      enqueueSnackbar(vars.user_id ? "Committee updated" : "Slot cleared", {
        variant: "success",
      });
      // Write the returned suggestion straight into the detail cache so
      // every seat re-renders off the saved roster before the refetch
      // lands — otherwise the row the user just filled flips back to
      // empty for one PUT + GET round-trip.
      qc.setQueryData(["ess-detail", suggestion.id], fresh);
      invalidateSuggestionCaches(qc, suggestion.id);
    },
    onError: (err: unknown) => {
      enqueueSnackbar(apiErrorMessage(err, "Could not update the committee"), {
        variant: "error",
      });
    },
  });

  const committees = workflow?.committees;
  if (!committees || authQ.data !== true) return null;

  const reviewers = suggestion.assigned_reviewers ?? [];
  // Only the seat whose save is in flight locks. A single `isPending` flag
  // froze every picker on the card, so filling a committee meant waiting
  // out a round-trip between each seat.
  const pending = mutation.isPending ? mutation.variables : undefined;

  return (
    <Card sx={{ mb: 2.5 }} data-tour="detail-committee">
      <CardContent>
        <SectionHeader title="Committees" />
        <Stack spacing={3}>
          {Object.entries(committees).map(([committee, config]) => (
            <Box key={committee}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontWeight: 600,
                }}
              >
                {config.label}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {config.slots.map((slot) => (
                  <SlotRow
                    key={slot.key}
                    slot={slot}
                    // Rows assigned before slots existed carry only a
                    // sub_role, so fall back to that rather than showing
                    // the seat as empty.
                    assigned={reviewers.find(
                      (r) =>
                        r.committee === committee &&
                        (r.slot
                          ? r.slot === slot.key
                          : r.sub_role === slot.sub_role),
                    )}
                    saving={
                      pending?.committee === committee &&
                      pending?.slot === slot.key
                    }
                    onPick={(user) =>
                      mutation.mutate({
                        committee,
                        slot: slot.key,
                        user_id: user?.id ?? null,
                      })
                    }
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
